import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../context/CurrencyContext";
import { useNotification } from "../context/NotificationContext";
import { useAuth } from "../context/AuthContext";
import { formatPrice } from "../utils/formatPrice";
import { formatNigerPhone } from "../utils/nigerPhone";
import { clearCart, getCartItems, removeCartItem } from "../utils/cart";
import {
  addAddress,
  getAddresses,
  removeAddress,
  setDefaultAddress,
} from "../utils/addressBook";
import api from "../services/api";
import { 
  FiMapPin, 
  FiPhone, 
  FiPlus, 
  FiTrash2, 
  FiCheckCircle, 
  FiShield, 
  FiChevronLeft, 
  FiShoppingBag,
  FiX,
  FiAlertCircle,
  FiTruck,
  FiCreditCard
} from "react-icons/fi";

const emptyAddress = {
  label: "",
  full_name: "",
  phone: "",
  line1: "",
  line2: "",
  city: "",
  region: "",
  postal_code: "",
  country: "Niger",
  is_default: false,
};

const CheckoutPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { currency, rates } = useCurrency();
  const { showNotification } = useNotification();
  const [items, setItems] = useState([]);
  const [addresses, setAddressesState] = useState([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [form, setForm] = useState(emptyAddress);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [placedOrders, setPlacedOrders] = useState([]);
  const buyNowItem = location.state?.buyNowItem || null;

  useEffect(() => {
    setItems(buyNowItem ? [buyNowItem] : getCartItems());
    setAddressesState(getAddresses());
    const syncAddresses = () => setAddressesState(getAddresses());
    window.addEventListener("chafadi-addresses-updated", syncAddresses);
    return () => window.removeEventListener("chafadi-addresses-updated", syncAddresses);
  }, [buyNowItem]);

  const total = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum + Number(item.price || item.max_price || item.min_price || 0) * (item.quantity || 1),
        0
      ),
    [items]
  );

  const defaultAddress = addresses.find((address) => address.is_default) || addresses[0];

  const updateItemQuantity = (itemId, nextQuantity) => {
    setItems((current) =>
      current.map((item) =>
        item.id === itemId
          ? { ...item, quantity: Math.max(1, Number(nextQuantity) || 1) }
          : item
      )
    );
  };

  const handleAddAddress = (e) => {
    e.preventDefault();
    addAddress({ ...form, phone: formatNigerPhone(form.phone) });
    setAddressesState(getAddresses());
    setForm(emptyAddress);
    setShowAddressModal(false);
    showNotification(t("profilePage.addressAddedSuccess") || "Address saved successfully", "success");
  };

  const handlePlaceOrder = async () => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (!defaultAddress) {
      showNotification(t("profilePage.addressRequired") || "Delivery address required", "warning");
      return;
    }
    if (items.length === 0) {
      showNotification(t("cartPage.emptyCart") || "Cart is empty", "warning");
      return;
    }

    setPlacingOrder(true);
    try {
      const response = await api.post("/orders/", {
        address: defaultAddress,
        items: items.map((item) => ({
          product_id: item.id,
          quantity: item.quantity || 1,
          unit_price: item.price || item.max_price || item.min_price,
          selected_selections: item.selected_selections || [],
        })),
      });
      const created = response.data.orders || [];
      setPlacedOrders(created);
      if (buyNowItem) {
        removeCartItem(buyNowItem.id);
      } else {
        clearCart();
      }
      setItems([]);
      showNotification(response.data.message || "Order placed successfully", "success");
    } catch (error) {
      showNotification(t("profilePage.orderError") || "Failed to place order", "danger");
    } finally {
      setPlacingOrder(false);
    }
  };

  if (placedOrders.length > 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-20 bg-[#fcfcfc]">
        <div className="bg-white rounded-[40px] border border-gray-100 p-8 md:p-16 shadow-2xl max-w-4xl w-full text-center relative overflow-hidden animate-zoom-in">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#ff7a00] to-[#ff9500]" />
          <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-sm">
            <FiCheckCircle size={40} />
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-gray-900 mb-4 tracking-tight">Order Received</h1>
          <p className="text-sm font-medium text-gray-400 mb-12 max-w-md mx-auto leading-relaxed">
            Thank you for your order! We've notified the sellers, and they will contact you directly to arrange payment and delivery.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left mb-12">
            {placedOrders.map((order) => (
              <div key={order.id} className="p-6 bg-gray-50 rounded-2xl border border-gray-100 group transition-all hover:bg-white hover:shadow-md">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Order ID: #{order.id}</span>
                  <span className="px-3 py-1 bg-[#ff7a00]/10 rounded-full text-[9px] font-bold uppercase tracking-widest text-[#ff7a00]">
                    {order.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[11px] font-bold text-gray-900 uppercase tracking-wide">
                   <FiShoppingBag size={14} className="text-[#ff7a00]" />
                   {order.items.length} {order.items.length === 1 ? "Product" : "Products"}
                </div>
                {Array.isArray(order.items?.[0]?.selected_selections) &&
                order.items[0].selected_selections.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {order.items[0].selected_selections.map((selection) => (
                      <span
                        key={`${order.id}-${selection.name}-${selection.value}`}
                        className="inline-flex items-center rounded-full bg-orange-50 border border-orange-100 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-[#ff7a00]"
                      >
                        {selection.name}: {selection.value}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button 
              onClick={() => navigate("/profile")} 
              className="h-14 px-10 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-all uppercase text-[11px] tracking-widest"
            >
              My Orders
            </button>
            <button 
              onClick={() => navigate("/")} 
              className="h-14 px-10 bg-white text-gray-900 font-bold rounded-xl border border-gray-200 hover:bg-gray-50 transition-all uppercase text-[11px] tracking-widest"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fcfcfc] pb-32">
      <div className="max-w-7xl mx-auto px-4 py-12 md:py-20">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-16 animate-fade-in">
          <div className="space-y-4">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[11px] font-bold text-gray-400 uppercase tracking-wider hover:text-[#ff7a00] transition-colors group">
              <FiChevronLeft className="group-hover:-translate-x-1 transition-transform" />
              {t("common.back") || "Back"}
            </button>
            <h1 className="text-4xl md:text-6xl font-black text-gray-900 tracking-tight">Checkout</h1>
          </div>
          <button 
            onClick={() => setShowAddressModal(true)}
            className="h-14 px-8 bg-white border border-gray-200 text-gray-900 rounded-xl font-bold text-[11px] hover:border-[#ff7a00] hover:text-[#ff7a00] transition-all shadow-sm uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <FiPlus size={16} />
            {t("profilePage.addProduct") || "New Address"}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          
          {/* Left Column: Delivery & Shipping */}
          <div className="lg:col-span-7 space-y-10 animate-slide-up delay-100">
            
            {/* Step 1: Shipping Address */}
            <section className="bg-white rounded-3xl border border-gray-100 p-8 md:p-10 shadow-sm relative overflow-hidden">
               <div className="flex items-center gap-4 mb-8">
                 <div className="w-1.5 h-6 bg-[#ff7a00] rounded-full" />
                 <h2 className="text-lg font-bold text-gray-900 uppercase tracking-tight">1. Shipping Address</h2>
              </div>
              
              {defaultAddress ? (
                <div className="space-y-8">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center text-[#ff7a00] border border-gray-100 shrink-0">
                       <FiMapPin size={24} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900 truncate">{defaultAddress.full_name}</h3>
                        <span className="inline-flex items-center px-2.5 py-0.5 bg-emerald-50 text-emerald-600 text-[9px] font-bold rounded-full uppercase tracking-widest border border-emerald-100">Default</span>
                      </div>
                      <p className="text-sm font-medium text-gray-500 leading-relaxed max-w-md">
                        {defaultAddress.line1}, {defaultAddress.line2 && defaultAddress.line2 + ", "}{defaultAddress.city}, {defaultAddress.region} {defaultAddress.postal_code}, {defaultAddress.country}
                      </p>
                      <div className="flex items-center gap-2 mt-4 text-[11px] font-bold text-gray-900 uppercase tracking-wide bg-gray-50 w-fit px-3 py-1.5 rounded-lg border border-gray-100">
                        <FiPhone className="text-[#ff7a00]" size={14} />
                        {defaultAddress.phone}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-16 text-center border border-dashed border-gray-200 rounded-2xl bg-gray-50">
                  <FiAlertCircle size={40} className="text-gray-300 mx-auto mb-4" />
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Please add a shipping address to proceed.</p>
                </div>
              )}

              {/* Saved Addresses Toggle (if multiple) */}
              {addresses.length > 1 && (
                <div className="mt-10 pt-8 border-t border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-6">Switch to another address</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {addresses.filter(a => a.id !== defaultAddress?.id).map((address) => (
                      <div 
                        key={address.id} 
                        onClick={() => setDefaultAddress(address.id)}
                        className="p-5 bg-gray-50 rounded-2xl border border-gray-100 hover:border-[#ff7a00] hover:bg-white transition-all cursor-pointer group"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <strong className="text-sm font-bold text-gray-900 truncate block">{address.label || address.full_name}</strong>
                          <button 
                            onClick={(e) => { e.stopPropagation(); removeAddress(address.id); }}
                            className="text-gray-300 hover:text-red-500 transition-colors"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                        <p className="text-[11px] text-gray-500 font-medium truncate mb-1">{address.city}, {address.region}</p>
                        <p className="text-[11px] font-bold text-[#ff7a00] uppercase tracking-wider group-hover:translate-x-1 transition-transform">Use this address →</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Step 2: Payment Method Info */}
            <section className="bg-white rounded-3xl border border-gray-100 p-8 md:p-10 shadow-sm relative overflow-hidden">
               <div className="flex items-center gap-4 mb-8">
                 <div className="w-1.5 h-6 bg-[#ff7a00] rounded-full" />
                 <h2 className="text-lg font-bold text-gray-900 uppercase tracking-tight">2. Payment Method</h2>
              </div>
              <div className="flex items-center gap-6 p-6 bg-[#ff7a00]/5 rounded-2xl border border-[#ff7a00]/10">
                 <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center text-[#ff7a00] shadow-sm">
                   <FiCreditCard size={26} />
                 </div>
                 <div>
                   <h4 className="text-sm font-bold text-gray-900 mb-1">Direct Settlement</h4>
                   <p className="text-[11px] font-medium text-gray-500 leading-relaxed uppercase tracking-wider">
                     Pay the sellers directly upon delivery or as per your agreement.
                   </p>
                 </div>
              </div>
            </section>
          </div>

          {/* Right Column: Order Summary (Sticky) */}
          <div className="lg:col-span-5 animate-slide-up delay-200">
            <div className="bg-white rounded-3xl p-8 md:p-10 border border-gray-100 shadow-xl lg:sticky lg:top-32">
               <div className="flex items-center gap-3 mb-8">
                 <FiShoppingBag className="text-[#ff7a00]" size={20} />
                 <h2 className="text-lg font-bold text-gray-900 uppercase tracking-tight">{t("cartPage.orderSummary")}</h2>
              </div>
              
              <div className="space-y-6 mb-8 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
                {items.map((item) => (
                  <div key={item.id} className="flex gap-4 items-start">
                    <div className="w-16 h-16 bg-gray-50 rounded-xl overflow-hidden border border-gray-100 shrink-0">
                      <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-bold text-gray-900 truncate">{item.name}</h4>
                      {Array.isArray(item.selected_selections) && item.selected_selections.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {item.selected_selections.map((selection) => (
                            <span
                              key={`${item.id}-${selection.name}-${selection.value}`}
                              className="inline-flex items-center rounded-full bg-orange-50 border border-orange-100 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-[#ff7a00]"
                            >
                              {selection.name}: {selection.value}
                            </span>
                          ))}
                        </div>
                      ) : null}
                          <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-medium text-gray-400">Qty</span>
                          <div className="inline-flex items-center rounded-xl bg-gray-50 border border-gray-100 overflow-hidden">
                            <button
                              type="button"
                              onClick={() => updateItemQuantity(item.id, (item.quantity || 1) - 1)}
                              className="w-8 h-8 flex items-center justify-center text-[#ff7a00] font-black"
                            >
                              -
                            </button>
                            <span className="w-10 text-center text-xs font-bold text-gray-900">
                              {item.quantity || 1}
                            </span>
                            <button
                              type="button"
                              onClick={() => updateItemQuantity(item.id, (item.quantity || 1) + 1)}
                              className="w-8 h-8 flex items-center justify-center text-[#ff7a00] font-black"
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <span className="text-sm font-black text-gray-900 tracking-tight">
                          {formatPrice(
                            Number(item.price || item.max_price || item.min_price || 0) * (item.quantity || 1),
                            currency,
                            rates
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="pt-8 border-t border-gray-100 space-y-4">
                <div className="flex justify-between items-center text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  <span>{t("cartPage.subtotal")}</span>
                  <span className="text-gray-900">{formatPrice(total, currency, rates)}</span>
                </div>
                <div className="flex justify-between items-center text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  <span>Shipping</span>
                  <div className="flex items-center gap-1.5 text-emerald-500">
                    <FiTruck size={14} />
                    <span>Free</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-end pt-4 mb-8">
                  <span className="text-xs font-bold text-gray-900 uppercase tracking-widest">{t("cartPage.total")}</span>
                  <div className="text-right">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter line-through mb-1 opacity-50">
                      {formatPrice(total * 1.1, currency, rates)}
                    </p>
                    <strong className="text-4xl font-black text-gray-900 tracking-tighter leading-none">
                      {formatPrice(total, currency, rates)}
                    </strong>
                  </div>
                </div>

                <button 
                  onClick={handlePlaceOrder} 
                  disabled={placingOrder}
                  className="w-full h-16 bg-[#ff7a00] text-white font-black text-xs uppercase tracking-[0.2em] rounded-2xl shadow-lg shadow-orange-100 hover:bg-[#e66e00] hover:shadow-orange-200 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3"
                >
                  {placingOrder ? (
                    <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <FiShield size={18} /> Complete Checkout
                    </>
                  )}
                </button>
                <div className="flex items-center justify-center gap-2 pt-4 opacity-40">
                  <FiShield size={14} className="text-emerald-500" />
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Buyer Protection Enabled</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ADDRESS MODAL */}
        {showAddressModal && (
          <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4">
            <div 
              className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm animate-fade-in"
              onClick={() => setShowAddressModal(false)}
            />
            <div className="relative bg-white rounded-[32px] w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl animate-zoom-in">
              <div className="flex flex-col h-full">
                {/* Modal Header */}
                <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10">
                   <div>
                     <h2 className="text-2xl font-black text-gray-900 tracking-tight">New Address</h2>
                     <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mt-1">Define your delivery location</p>
                   </div>
                  <button onClick={() => setShowAddressModal(false)} className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-900 transition-all">
                    <FiX size={20} />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-8 overflow-y-auto custom-scrollbar">
                  <form onSubmit={handleAddAddress} className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                         <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider ml-1">Address Name</label>
                         <input 
                          placeholder="e.g. Home, Work" 
                          value={form.label} 
                          onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
                          className="w-full h-12 bg-gray-50 border border-gray-100 focus:bg-white focus:border-[#ff7a00] rounded-xl px-5 text-sm font-medium text-gray-900 outline-none transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider ml-1">Recipient Name *</label>
                         <input 
                          placeholder="Full Name" 
                          value={form.full_name} 
                          onChange={(e) => setForm((prev) => ({ ...prev, full_name: e.target.value }))} 
                          required 
                          className="w-full h-12 bg-gray-50 border border-gray-100 focus:bg-white focus:border-[#ff7a00] rounded-xl px-5 text-sm font-medium text-gray-900 outline-none transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider ml-1">Phone Number *</label>
                      <input 
                        placeholder="Contact Number" 
                        value={form.phone} 
                        onChange={(e) => setForm((prev) => ({ ...prev, phone: formatNigerPhone(e.target.value) }))} 
                        required 
                        className="w-full h-12 bg-gray-50 border border-gray-100 focus:bg-white focus:border-[#ff7a00] rounded-xl px-5 text-sm font-medium text-gray-900 outline-none transition-all"
                      />
                    </div>

                    <div className="space-y-4">
                      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider ml-1">Street Address *</label>
                      <div className="space-y-3">
                        <input 
                          placeholder="Street Address, P.O. Box" 
                          value={form.line1} 
                          onChange={(e) => setForm((prev) => ({ ...prev, line1: e.target.value }))} 
                          required 
                          className="w-full h-12 bg-gray-50 border border-gray-100 focus:bg-white focus:border-[#ff7a00] rounded-xl px-5 text-sm font-medium text-gray-900 outline-none transition-all"
                        />
                        <input 
                          placeholder="Apartment, Suite, Unit, etc. (Optional)" 
                          value={form.line2} 
                          onChange={(e) => setForm((prev) => ({ ...prev, line2: e.target.value }))} 
                          className="w-full h-12 bg-gray-50 border border-gray-100 focus:bg-white focus:border-[#ff7a00] rounded-xl px-5 text-sm font-medium text-gray-900 outline-none transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                         <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider ml-1">City *</label>
                         <input 
                          placeholder="City" 
                          value={form.city} 
                          onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))} 
                          required 
                          className="w-full h-12 bg-gray-50 border border-gray-100 focus:bg-white focus:border-[#ff7a00] rounded-xl px-5 text-sm font-medium text-gray-900 outline-none transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider ml-1">State / Region *</label>
                         <input 
                          placeholder="Region" 
                          value={form.region} 
                          onChange={(e) => setForm((prev) => ({ ...prev, region: e.target.value }))} 
                          required 
                          className="w-full h-12 bg-gray-50 border border-gray-100 focus:bg-white focus:border-[#ff7a00] rounded-xl px-5 text-sm font-medium text-gray-900 outline-none transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                         <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider ml-1">Postal Code</label>
                         <input 
                          placeholder="ZIP Code" 
                          value={form.postal_code} 
                          onChange={(e) => setForm((prev) => ({ ...prev, postal_code: e.target.value }))} 
                          className="w-full h-12 bg-gray-50 border border-gray-100 focus:bg-white focus:border-[#ff7a00] rounded-xl px-5 text-sm font-medium text-gray-900 outline-none transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider ml-1">Country</label>
                         <input 
                          value={form.country} 
                          readOnly
                          className="w-full h-12 bg-gray-100 border border-gray-100 rounded-xl px-5 text-sm font-bold text-gray-400 outline-none cursor-not-allowed"
                        />
                      </div>
                    </div>

                    <label className="flex items-center gap-3 cursor-pointer group w-fit">
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={form.is_default}
                          onChange={(e) => setForm((prev) => ({ ...prev, is_default: e.target.checked }))}
                        />
                        <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-[#ff7a00] transition-colors after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-transform peer-checked:after:translate-x-5"></div>
                      </div>
                      <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wider">Set as default address</span>
                    </label>

                    <div className="flex gap-4 pt-4">
                      <button 
                        type="button"
                        onClick={() => setShowAddressModal(false)}
                        className="flex-1 h-12 bg-gray-50 text-gray-900 font-bold rounded-xl hover:bg-gray-100 transition-all uppercase text-[11px] tracking-widest border border-gray-200"
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit"
                        className="flex-1 h-12 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-all uppercase text-[11px] tracking-widest"
                      >
                        Save Address
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;
